import { v4 as uuid } from 'uuid';
import { parseInclusionShare, type PlanumInclusion } from '@ast-data/shared';

/** An inclusion row in the editor — a PlanumInclusion plus a stable local key. */
export type InclusionRow = {
    id: string;
    inclusion: string;
    percentage: string;
};

/** Wrap stored inclusions as editor rows (on open / reset). */
export function toInclusionRows(
    inclusions: readonly PlanumInclusion[] | undefined
): InclusionRow[] {
    return (inclusions ?? []).map((entry) => ({ id: uuid(), ...entry }));
}

/** Strip the local key + empty rows, leaving the storable list. */
export function cleanInclusions(
    rows: readonly InclusionRow[]
): PlanumInclusion[] {
    return rows
        .map((row) => ({
            inclusion: row.inclusion.trim(),
            /**
             * Stored bare, exactly as an import stores it — a share typed as
             * "10%" and one typed as "10" must not end up as two different
             * records. The sign goes back on when the value is rendered.
             */
            percentage: parseInclusionShare(row.percentage)
        }))
        .filter((entry) => entry.inclusion !== '' || entry.percentage !== '');
}

/** Stable, order-sensitive key for the inclusion list (dirty comparison). */
export function inclusionsKey(
    rows: readonly { inclusion: string; percentage: string }[]
): string {
    return JSON.stringify(
        rows.map((row) => [row.inclusion.trim(), row.percentage.trim()])
    );
}
